// Data service layer. Replace the bodies of these functions with real API calls later;
// keep the returned shapes (see src/types/orchard.js) so the UI does not change.
import { STATIONS, getStationById } from "@/data/stations";
import {
  STATION_DATASETS,
  DEMO_DATE,
  CHILL_SEASON_LENGTH,
  CHILL_SEASON_START,
  PRIOR_CHILL_SEASONS,
  LATEST_CHILL_SEASON,
  UPCOMING_CHILL_SEASON,
  PRIOR_GDD_SEASONS,
  CURRENT_GDD_SEASON,
  GDD_CURRENT_INDEX,
  GDD_SEASON_LENGTH,
} from "@/data/mockOrchardData";
import { CONFIDENCE_CONFIG, getConfidence } from "@/config/confidence";
import { distanceMi, idwWeights } from "@/lib/geo";
import { addDaysIso, daysBetween, shortDate } from "@/lib/format";

const LATENCY_MS = 380;
const respond = (value) => new Promise((resolve) => setTimeout(() => resolve(value), LATENCY_MS));

export class OutOfCoverageError extends Error {
  constructor(distance) {
    super(`This location is about ${Math.round(distance)} mi from the nearest station — outside demo coverage.`);
    this.name = "OutOfCoverageError";
  }
}

const sum = (a) => a.reduce((s, v) => s + v, 0);
const cumulative = (a) => {
  let s = 0;
  return a.map((v) => (s += v));
};
const stats = (vals) => ({ min: Math.min(...vals), max: Math.max(...vals), avg: sum(vals) / vals.length });

const blend = (objs, ws) => {
  if (Array.isArray(objs[0])) return objs[0].map((_, i) => objs.reduce((s, a, k) => s + a[i] * ws[k], 0));
  return Object.fromEntries(Object.keys(objs[0]).map((k) => [k, blend(objs.map((o) => o[k]), ws)]));
};

export const getStations = () => respond(STATIONS);

export function resolveLocation(selection) {
  if (selection.type === "station") {
    const s = getStationById(selection.stationId);
    return {
      kind: "station",
      id: s.id,
      name: s.name,
      region: s.region,
      lat: s.lat,
      lng: s.lng,
      nearest: { station: s, distanceMi: 0 },
      weights: [{ id: s.id, weight: 1 }],
      confidence: getConfidence(0, "station"),
    };
  }
  const { lat, lng } = selection;
  const ranked = STATIONS.map((s) => ({ station: s, distanceMi: distanceMi({ lat, lng }, s) })).sort(
    (a, b) => a.distanceMi - b.distanceMi,
  );
  const nearest = ranked[0];
  if (nearest.distanceMi > CONFIDENCE_CONFIG.coverageLimitMi) throw new OutOfCoverageError(nearest.distanceMi);
  return {
    kind: "estimated",
    id: "custom",
    name: "Custom orchard site",
    region: `Estimated from ${STATIONS.length} stations`,
    lat,
    lng,
    nearest,
    weights: idwWeights(ranked, CONFIDENCE_CONFIG.idwPower),
    confidence: getConfidence(nearest.distanceMi, "estimated"),
  };
}

const datasetCache = new Map();
const datasetFor = (location) => {
  const key = location.weights.map((w) => `${w.id}:${w.weight.toFixed(6)}`).join("|");
  if (!datasetCache.has(key)) {
    datasetCache.set(
      key,
      blend(
        location.weights.map((w) => STATION_DATASETS[w.id]),
        location.weights.map((w) => w.weight),
      ),
    );
  }
  return datasetCache.get(key);
};

const prepare = (selection) => {
  const location = resolveLocation(selection);
  return { location, ds: datasetFor(location) };
};

const DAILY_KEY = { chillHours: "dailyHours", chillPortions: "dailyPortions" };

export async function getLocation(selection) {
  return respond(resolveLocation(selection));
}

export async function getSeasonSummary(selection) {
  const { location, ds } = prepare(selection);
  const chill = (metric) => {
    const key = DAILY_KEY[metric];
    const prior = PRIOR_CHILL_SEASONS.map((s) => sum(ds.chill[s][key]));
    return {
      latestSeason: LATEST_CHILL_SEASON,
      value: sum(ds.chill[LATEST_CHILL_SEASON][key]),
      upcomingSeason: UPCOMING_CHILL_SEASON,
      seasonToDate: 0,
      daysUntilStart: daysBetween(DEMO_DATE, CHILL_SEASON_START),
      priorSeasons: PRIOR_CHILL_SEASONS,
      ...stats(prior),
    };
  };
  const gddPrior = PRIOR_GDD_SEASONS.map((y) => sum(ds.gdd[y].slice(0, GDD_CURRENT_INDEX + 1)));
  return respond({
    asOf: DEMO_DATE,
    location,
    chillHours: chill("chillHours"),
    chillPortions: chill("chillPortions"),
    gdd: {
      season: CURRENT_GDD_SEASON,
      value: sum(ds.gdd[CURRENT_GDD_SEASON].slice(0, GDD_CURRENT_INDEX + 1)),
      priorSeasons: PRIOR_GDD_SEASONS,
      ...stats(gddPrior),
    },
  });
}

const buildHistoricalPoints = ({ length, startIso, priorSeries, currentSeries, currentUntil }) =>
  Array.from({ length }, (_, i) => {
    const vals = priorSeries.map((s) => s[i]);
    const st = stats(vals);
    return {
      i,
      label: shortDate(addDaysIso(startIso, i)),
      current: i <= currentUntil ? currentSeries[i] : null,
      avg: st.avg,
      range: [st.min, st.max],
      ...Object.fromEntries(vals.map((v, k) => [`s${k}`, v])),
    };
  });

export async function getHistoricalSeries(selection, metric) {
  const { location, ds } = prepare(selection);
  if (metric === "gdd") {
    return respond({
      metric,
      location,
      currentLabel: `${CURRENT_GDD_SEASON} season-to-date`,
      priorLabels: PRIOR_GDD_SEASONS,
      todayLabel: shortDate(DEMO_DATE),
      points: buildHistoricalPoints({
        length: GDD_SEASON_LENGTH,
        startIso: "2026-01-01",
        priorSeries: PRIOR_GDD_SEASONS.map((y) => cumulative(ds.gdd[y])),
        currentSeries: cumulative(ds.gdd[CURRENT_GDD_SEASON]),
        currentUntil: GDD_CURRENT_INDEX,
      }),
      note: "The 2026 heat season runs January 1 through December 31; observations shown through October 24.",
    });
  }
  const key = DAILY_KEY[metric];
  return respond({
    metric,
    location,
    currentLabel: `${LATEST_CHILL_SEASON} (latest completed)`,
    priorLabels: PRIOR_CHILL_SEASONS,
    todayLabel: null,
    points: buildHistoricalPoints({
      length: CHILL_SEASON_LENGTH,
      startIso: "2025-11-01",
      priorSeries: PRIOR_CHILL_SEASONS.map((s) => cumulative(ds.chill[s][key])),
      currentSeries: cumulative(ds.chill[LATEST_CHILL_SEASON][key]),
      currentUntil: CHILL_SEASON_LENGTH,
    }),
    note: `The ${UPCOMING_CHILL_SEASON} chill season begins November 1, 2026. The most recent completed season (${LATEST_CHILL_SEASON}) is compared with the four seasons before it.`,
  });
}

const Z80 = 1.2816;
const SIGMA = {
  gdd: (lead) => (lead <= 7 ? 0.8 + 0.35 * lead : 3.2 + 0.12 * (lead - 7)),
  chillHours: (lead, mean) => (mean > 0 ? 1.5 + 0.45 * mean : 0),
  chillPortions: (lead, mean) => (mean > 0 ? 0.15 + 0.4 * mean : 0),
};

const forecastMeans = (ds, metric, horizon) =>
  Array.from({ length: horizon }, (_, k) => {
    const lead = k + 1;
    if (metric === "gdd") {
      const idx = GDD_CURRENT_INDEX + lead;
      return lead <= 7 ? 0.6 * ds.gdd[CURRENT_GDD_SEASON][idx] + 0.4 * ds.gddClimatology[idx] : ds.gddClimatology[idx];
    }
    const ci = daysBetween(CHILL_SEASON_START, addDaysIso(DEMO_DATE, lead));
    if (ci < 0) return 0;
    const seasons = [...PRIOR_CHILL_SEASONS, LATEST_CHILL_SEASON];
    return sum(seasons.map((s) => ds.chill[s][DAILY_KEY[metric]][ci])) / seasons.length;
  });

const buildMetricForecast = (ds, metric, horizon) => {
  const observedDates = Array.from({ length: 14 }, (_, k) => addDaysIso(DEMO_DATE, k - 13));
  const gddCum = cumulative(ds.gdd[CURRENT_GDD_SEASON]);
  const observed = observedDates.map((_, k) => (metric === "gdd" ? gddCum[GDD_CURRENT_INDEX - 13 + k] : 0));
  const start = observed[observed.length - 1];
  let acc = start;
  let variance = 0;
  const forecast = forecastMeans(ds, metric, horizon).map((mean, k) => {
    const lead = k + 1;
    const date = addDaysIso(DEMO_DATE, lead);
    acc += mean;
    variance += SIGMA[metric](lead, mean) ** 2;
    const half = Z80 * Math.sqrt(variance);
    return { date, label: shortDate(date), lead, forecast: acc, range: [Math.max(start, acc - half), acc + half] };
  });
  const observedPoints = observedDates.map((date, k) => ({
    date,
    label: shortDate(date),
    observed: observed[k],
    ...(k === 13 ? { forecast: start, range: [start, start] } : {}),
  }));
  const end = forecast[forecast.length - 1];
  return {
    metric,
    current: start,
    projected: end.forecast,
    low: end.range[0],
    high: end.range[1],
    added: end.forecast - start,
    points: [...observedPoints, ...forecast],
  };
};

export async function getForecast(selection, horizon) {
  const { location, ds } = prepare(selection);
  return respond({
    asOf: DEMO_DATE,
    horizon,
    location,
    chillSeasonStart: CHILL_SEASON_START,
    todayLabel: shortDate(DEMO_DATE),
    metrics: {
      chillHours: buildMetricForecast(ds, "chillHours", horizon),
      chillPortions: buildMetricForecast(ds, "chillPortions", horizon),
      gdd: buildMetricForecast(ds, "gdd", horizon),
    },
  });
}
